import { Stack, Group, Button, Text } from "@mantine/core";
import { IconMailbox, IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { displayLastUpdated } from "../utils/display";
import type { OutgoingMailboxStatus } from "../utils/types";

interface EmailSyncSubTabProps {
  syncStatus:
    | {
        sync_state: string;
        has_error: boolean;
        last_error?: string | null;
        last_successful_sync_at?: string | null;
        last_attempted_sync_at?: string | null;
        integration_status: string;
      }
    | null
    | undefined;
  outgoingMailbox: OutgoingMailboxStatus | null | undefined;
  userInfo:
    | {
        oauth_provider?: string | null;
      }
    | null
    | undefined;
  handleReAuthenticateEmail: () => void;
  onSyncNow: () => void;
  lastE2eRefresh?: string;
  isProcessingEmails?: boolean;
}

export function EmailSyncSubTab({
  syncStatus,
  outgoingMailbox,
  userInfo,
  handleReAuthenticateEmail,
  onSyncNow,
  lastE2eRefresh,
  isProcessingEmails = false,
}: EmailSyncSubTabProps) {
  const isSyncing = syncStatus?.sync_state === "SYNCING";
  const usesSharedMailbox = Boolean(outgoingMailbox?.uses_official_email);
  const collectionEmail = usesSharedMailbox
    ? outgoingMailbox?.official_outgoing_email
    : outgoingMailbox?.mailbox_email;
  const canSync = usesSharedMailbox
    ? Boolean(collectionEmail?.trim()) &&
      Boolean(outgoingMailbox?.mailbox_user_id)
    : Boolean(outgoingMailbox?.mailbox_connected);
  const isNonOwnerSharedMailbox =
    usesSharedMailbox && !outgoingMailbox?.acting_user_is_mailbox_owner;
  const orgMailboxHealthy =
    !usesSharedMailbox || Boolean(outgoingMailbox?.mailbox_connected);
  const showOrgMailboxIssue =
    usesSharedMailbox && outgoingMailbox && !outgoingMailbox.mailbox_connected;
  const showProcessing =
    isProcessingEmails && !isSyncing && !syncStatus?.has_error;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-linear-to-br from-indigo-500 to-violet-500 text-white shadow-md">
          <IconMailbox size={24} stroke={2} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Email Sync Status
          </h2>
          <p className="text-sm text-gray-600">
            {usesSharedMailbox
              ? "Monitor the shared collections mailbox"
              : "Monitor your personal mailbox sync"}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <Text size="sm" fw={600} c="indigo.9" mb={4}>
          {usesSharedMailbox ? "Outgoing email (collections)" : "Your mailbox"}
        </Text>
        {outgoingMailbox?.from_header ? (
          <Text size="sm" c="gray.8">
            {outgoingMailbox.from_header}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            {usesSharedMailbox
              ? "No collections email configured. An admin can set this under Email settings."
              : "Connect your email below to sync your inbox."}
          </Text>
        )}
        {usesSharedMailbox &&
          !outgoingMailbox?.acting_user_is_mailbox_owner &&
          outgoingMailbox?.from_header && (
            <Text size="xs" c="dimmed" mt={6}>
              You can send on behalf of this mailbox when it is connected.
            </Text>
          )}
      </div>

      {showOrgMailboxIssue && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <Text size="sm" fw={600} c="orange.9" mb={4}>
            Shared mailbox not ready
          </Text>
          <Text size="sm" c="orange.9">
            {outgoingMailbox.issue_message ??
              "The organization outgoing mailbox must be connected before sending."}
          </Text>
          {outgoingMailbox.acting_user_is_mailbox_owner && (
            <Text size="xs" c="dimmed" mt={6}>
              Use Re-authenticate below to connect this mailbox.
            </Text>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-6">
        <Text size="sm" fw={600} c="gray.8" mb="md">
          {usesSharedMailbox ? "Collections mailbox sync" : "Your mailbox sync"}
        </Text>
        {!canSync && (
          <Text size="sm" c="dimmed" mb="md">
            {usesSharedMailbox
              ? "Ask an admin to configure a collections email before syncing."
              : "Connect your email account before syncing."}
          </Text>
        )}
        <Stack gap="md">
          {syncStatus ? (
            <>
              <div className="flex items-center gap-3">
                {syncStatus.has_error ? (
                  <IconAlertCircle className="text-red-600" size={24} />
                ) : isSyncing ? null : (
                  <IconCheck className="text-green-600" size={24} />
                )}
                <div className="flex-1">
                  <Text
                    size="sm"
                    fw={600}
                    c={
                      syncStatus.has_error
                        ? "red"
                        : isSyncing || showProcessing
                          ? "blue"
                          : "green"
                    }
                  >
                    Status:{" "}
                    {syncStatus.has_error
                      ? "Error"
                      : isSyncing
                        ? "Syncing mailbox…"
                        : showProcessing
                          ? "Processing new emails…"
                          : "Healthy"}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    Integration: {syncStatus.integration_status}
                  </Text>
                </div>
              </div>

              {syncStatus.last_error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <Text size="sm" fw={600} c="red" mb={4}>
                    Last Error:
                  </Text>
                  <Text size="sm" c="red.7">
                    {syncStatus.last_error}
                  </Text>
                </div>
              )}

              <div className="space-y-2">
                <Text size="sm" c="gray.7">
                  Last successful sync:{" "}
                  <span className="font-semibold text-gray-800">
                    {syncStatus.last_successful_sync_at
                      ? displayLastUpdated(syncStatus.last_successful_sync_at)
                      : "Never"}
                  </span>
                </Text>
                <Text size="sm" c="gray.7">
                  Last attempted sync:{" "}
                  <span className="font-semibold text-gray-800">
                    {syncStatus.last_attempted_sync_at
                      ? displayLastUpdated(syncStatus.last_attempted_sync_at)
                      : "Never"}
                  </span>
                </Text>
              </div>
            </>
          ) : (
            <Text size="sm" c="dimmed">
              Loading sync status...
            </Text>
          )}
          {lastE2eRefresh && (
            <Text size="sm" c="gray.7">
              Last scheduled full org refresh:{" "}
              <span className="font-semibold text-gray-800">
                {displayLastUpdated(lastE2eRefresh)}
              </span>
            </Text>
          )}

          {showProcessing && (
            <Text size="sm" c="blue.7">
              New messages are being analyzed. Invoice and draft updates may
              appear over the next few minutes.
            </Text>
          )}

          {isNonOwnerSharedMailbox && canSync && (
            <Text size="sm" c="gray.7">
              Sync now updates the shared collections mailbox above, not your
              personal inbox.
            </Text>
          )}

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              onClick={onSyncNow}
              disabled={isSyncing || showProcessing || !canSync}
              color="indigo"
              variant="light"
              size="md"
              className="shadow-md hover:shadow-lg transition-shadow"
            >
              Sync now
            </Button>
            <Button
              onClick={handleReAuthenticateEmail}
              disabled={!userInfo?.oauth_provider}
              color="indigo"
              size="md"
              className="shadow-md hover:shadow-lg transition-shadow"
            >
              {outgoingMailbox?.acting_user_is_mailbox_owner &&
              usesSharedMailbox
                ? "Re-authenticate shared mailbox"
                : "Re-authenticate Email Account"}
            </Button>
          </Group>
        </Stack>
      </div>

      {usesSharedMailbox &&
        orgMailboxHealthy &&
        !outgoingMailbox?.acting_user_is_mailbox_owner && (
          <Text size="xs" c="dimmed">
            Sending and sync use the shared collections mailbox above.
          </Text>
        )}
    </div>
  );
}
